import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import { API_URL } from '../lib/utils';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Input } from './ui/input';

/**
 * Async company picker.
 *
 * Loads matches as the user types (debounced 250ms). Avoids dumping thousands
 * of options into the DOM. The selected company is always shown in the
 * trigger, regardless of which page of results it lives on.
 *
 * Props:
 *   value           - current selected company id (string)
 *   onChange(id)    - called with the new selected id (or '' to clear)
 *   selectedCompany - optional preloaded {id, name} for the current value so
 *                     we can render the trigger label without an extra fetch
 *   placeholder     - placeholder shown when nothing is selected
 *   disabled        - disables the trigger
 */
export const CompanyPicker = ({
  value,
  onChange,
  selectedCompany,
  placeholder = 'Select a company',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvedSelected, setResolvedSelected] = useState(selectedCompany || null);
  const debounceRef = useRef(null);

  // Keep the resolved-selected company label fresh if parent passes a new one
  useEffect(() => {
    if (selectedCompany) {
      setResolvedSelected(selectedCompany);
    }
  }, [selectedCompany]);

  // If we have a value but no resolved label yet, fetch it once
  useEffect(() => {
    if (!value) {
      setResolvedSelected(null);
      return;
    }
    if (resolvedSelected && resolvedSelected.id === value) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/companies/lookup`, {
          params: { ids: value },
        });
        if (cancelled) return;
        const found = (res.data?.items || []).find((c) => c.id === value);
        if (found) setResolvedSelected(found);
      } catch (err) {
        // non-fatal — fall back to id
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Search effect (debounced)
  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: 50 };
        if (search.trim().length > 0) params.search = search.trim();
        const res = await axios.get(`${API_URL}/companies/lookup`, { params });
        const list = res.data?.items || [];
        setItems(list);
      } catch (err) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, search.trim().length > 0 ? 250 : 0);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, open]);

  const triggerLabel = useMemo(() => {
    if (resolvedSelected && resolvedSelected.id === value) return resolvedSelected.name;
    if (value) return value;
    return placeholder;
  }, [value, resolvedSelected, placeholder]);

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
          data-testid="company-picker-trigger"
        >
          <span className={!value ? 'text-slate-400 font-normal' : 'font-normal'}>
            {triggerLabel}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]"
      >
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              autoFocus
              placeholder="Type to search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
              data-testid="company-picker-search"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Searching...
            </div>
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500">
              {search.trim() ? 'No companies match your search' : 'Start typing to search'}
            </div>
          ) : (
            items.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => handleSelect(c.id)}
                className={`w-full text-left flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-100 ${
                  c.id === value ? 'bg-emerald-50' : ''
                }`}
                data-testid={`company-picker-option-${c.id}`}
              >
                <span className="truncate">
                  {c.name}
                  {c.category && (
                    <span className="ml-2 text-xs text-slate-400">({c.category})</span>
                  )}
                </span>
                {c.id === value && <Check className="w-4 h-4 text-emerald-600" />}
              </button>
            ))
          )}
        </div>
        {value && (
          <div className="p-2 border-t">
            <button
              type="button"
              onClick={() => handleSelect('')}
              className="w-full text-left text-xs text-rose-600 hover:bg-rose-50 px-2 py-1.5 rounded flex items-center gap-1"
              data-testid="company-picker-clear"
            >
              <X className="w-3.5 h-3.5" /> Clear selection
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default CompanyPicker;
